import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Globe, FileText, Settings, BarChart3, Link2, Search, Plus, 
  Trash2, Edit, Eye, Check, X, RefreshCw, Download, Upload,
  TrendingUp, AlertTriangle, CheckCircle, FileCode, Map
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ProgrammaticSEOPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [pages, setPages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPageType, setSelectedPageType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Form states
  const [showPageForm, setShowPageForm] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  
  // Page form
  const [pageForm, setPageForm] = useState({
    page_type: 'tool',
    slug: '',
    title: '',
    meta_title: '',
    meta_description: '',
    h1: '',
    intro: '',
    robots_meta: 'index, follow',
    status: 'draft'
  });
  
  // Bulk generation form
  const [bulkForm, setBulkForm] = useState({
    page_type: 'tool',
    variables_csv: '',
    auto_publish: false
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pseo/dashboard/stats`);
      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const fetchPages = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API_URL}/api/pseo/pages?limit=100`;
      if (selectedPageType !== 'all') url += `&page_type=${selectedPageType}`;
      if (selectedStatus !== 'all') url += `&status=${selectedStatus}`;
      
      const res = await fetch(url);
      const data = await res.json();
      setPages(data.pages || []);
    } catch (err) {
      console.error('Failed to fetch pages:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPageType, selectedStatus]);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/pseo/templates`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPages();
    fetchTemplates();
  }, [fetchStats, fetchPages, fetchTemplates]);

  const seedTemplates = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/pseo/seed/templates`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Templates created successfully!');
        fetchTemplates();
      }
    } catch (err) {
      toast.error('Failed to seed templates');
    } finally {
      setLoading(false);
    }
  };

  const seedSamplePages = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/pseo/seed/sample-pages`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Created ${data.pages_created} sample pages!`);
        fetchPages();
        fetchStats();
      }
    } catch (err) {
      toast.error('Failed to seed sample pages');
    } finally {
      setLoading(false);
    }
  };

  const createPage = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/pseo/pages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(pageForm)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Page created successfully!');
        setShowPageForm(false);
        setPageForm({
          page_type: 'tool', slug: '', title: '', meta_title: '',
          meta_description: '', h1: '', intro: '', robots_meta: 'index, follow', status: 'draft'
        });
        fetchPages();
        fetchStats();
      } else {
        toast.error(data.detail || 'Failed to create page');
      }
    } catch (err) {
      toast.error('Failed to create page');
    } finally {
      setLoading(false);
    }
  };

  const deletePage = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/pseo/pages/${slug}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Page deleted!');
        fetchPages();
        fetchStats();
      }
    } catch (err) {
      toast.error('Failed to delete page');
    }
  };

  const publishPage = async (slug) => {
    try {
      const res = await fetch(`${API_URL}/api/pseo/pages/${slug}/publish`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Page published!');
        fetchPages();
        fetchStats();
      }
    } catch (err) {
      toast.error('Failed to publish page');
    }
  };

  const generateAllTools = async () => {
    if (!window.confirm('Generate SEO pages for all 75+ tools, industries, locations, and glossary terms? This will create ~200 pages.')) return;
    try {
      setLoading(true);
      toast.info('Generating pages... This may take a minute.');
      const res = await fetch(`${API_URL}/api/pseo/seed/all-tools`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success(`Created ${data.total_created} SEO pages!`);
        fetchPages();
        fetchStats();
      }
    } catch (err) {
      toast.error('Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const bulkGenerate = async () => {
    try {
      setLoading(true);
      // Parse CSV to variables list
      const lines = bulkForm.variables_csv.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim());
      const variables_list = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const obj = {};
        headers.forEach((h, i) => { obj[h] = values[i] || ''; });
        return obj;
      });

      const res = await fetch(`${API_URL}/api/pseo/generate/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page_type: bulkForm.page_type,
          variables_list,
          auto_publish: bulkForm.auto_publish
        })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Created ${data.created} pages!`);
        setShowBulkForm(false);
        setBulkForm({ page_type: 'tool', variables_csv: '', auto_publish: false });
        fetchPages();
        fetchStats();
      }
    } catch (err) {
      toast.error('Bulk generation failed');
    } finally {
      setLoading(false);
    }
  };

  const pageTypes = ['tool', 'usecase', 'comparison', 'best', 'industry', 'location', 'glossary', 'solution'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
            <Globe className="w-8 h-8 text-violet-600" />
            Programmatic SEO Manager
          </h1>
          <p className="text-slate-600 mt-1">Generate and manage thousands of SEO-optimized pages</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white shadow-sm">
            <TabsTrigger value="dashboard" className="gap-2">
              <BarChart3 className="w-4 h-4" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="pages" className="gap-2">
              <FileText className="w-4 h-4" /> Pages
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2">
              <FileCode className="w-4 h-4" /> Templates
            </TabsTrigger>
            <TabsTrigger value="sitemap" className="gap-2">
              <Map className="w-4 h-4" /> Sitemap
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="w-4 h-4" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            {stats ? (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-violet-100">Total Pages</p>
                          <p className="text-4xl font-bold mt-2">{stats.total_pages}</p>
                        </div>
                        <FileText className="w-8 h-8 opacity-80" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-green-100">Published</p>
                          <p className="text-4xl font-bold mt-2">{stats.published}</p>
                        </div>
                        <CheckCircle className="w-8 h-8 opacity-80" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-amber-500 to-orange-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-amber-100">Drafts</p>
                          <p className="text-4xl font-bold mt-2">{stats.draft}</p>
                        </div>
                        <Edit className="w-8 h-8 opacity-80" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
                    <CardContent className="p-6">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-blue-100">In Sitemap</p>
                          <p className="text-4xl font-bold mt-2">{stats.in_sitemap}</p>
                        </div>
                        <Map className="w-8 h-8 opacity-80" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Pages by Type */}
                <Card>
                  <CardHeader>
                    <CardTitle>Pages by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {Object.entries(stats.by_type || {}).map(([type, count]) => (
                        <div key={type} className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-500 capitalize">{type}</p>
                          <p className="text-2xl font-bold text-slate-800">{count}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Quality & Issues */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Content Quality
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">High Quality (80+)</span>
                          <Badge className="bg-green-100 text-green-700">{stats.quality?.high || 0}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Medium Quality (50-79)</span>
                          <Badge className="bg-amber-100 text-amber-700">{stats.quality?.medium || 0}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Low Quality (&lt;50)</span>
                          <Badge className="bg-red-100 text-red-700">{stats.quality?.low || 0}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        SEO Issues
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Missing Meta Description</span>
                          <Badge variant="destructive">{stats.issues?.missing_meta_description || 0}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">Missing H1</span>
                          <Badge variant="destructive">{stats.issues?.missing_h1 || 0}</Badge>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-600">No Internal Links</span>
                          <Badge variant="destructive">{stats.issues?.no_internal_links || 0}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-3">
                    <Button onClick={seedTemplates} disabled={loading} variant="outline">
                      <Plus className="w-4 h-4 mr-2" /> Seed Default Templates
                    </Button>
                    <Button onClick={seedSamplePages} disabled={loading} variant="outline">
                      <Plus className="w-4 h-4 mr-2" /> Create Sample Pages
                    </Button>
                    <Button onClick={generateAllTools} disabled={loading} className="bg-green-600 hover:bg-green-700">
                      <Plus className="w-4 h-4 mr-2" /> Generate All 200+ Pages
                    </Button>
                    <Button onClick={() => setShowBulkForm(true)} className="bg-violet-600 hover:bg-violet-700">
                      <Upload className="w-4 h-4 mr-2" /> Bulk Generate
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-violet-600" />
                <p className="mt-2 text-slate-600">Loading statistics...</p>
              </div>
            )}
          </TabsContent>

          {/* Pages Tab */}
          <TabsContent value="pages">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>SEO Pages</CardTitle>
                  <CardDescription>Manage all programmatic SEO pages</CardDescription>
                </div>
                <Button onClick={() => setShowPageForm(true)} className="bg-violet-600 hover:bg-violet-700">
                  <Plus className="w-4 h-4 mr-2" /> Add Page
                </Button>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="flex gap-4 mb-6">
                  <Select value={selectedPageType} onValueChange={setSelectedPageType}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {pageTypes.map(type => (
                        <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchPages}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>

                {/* Pages Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Slug</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Title</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Quality</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Status</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pages.map((page, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <code className="text-sm bg-slate-100 px-2 py-1 rounded">/{page.slug}</code>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant="outline" className="capitalize">{page.page_type}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700 max-w-xs truncate">{page.title}</td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={
                              page.quality_score >= 80 ? 'bg-green-100 text-green-700' :
                              page.quality_score >= 50 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }>
                              {page.quality_score || 0}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge className={page.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}>
                              {page.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex justify-center gap-2">
                              <Button size="sm" variant="ghost" onClick={() => window.open(`/${page.slug}`, '_blank')}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              {page.status === 'draft' && (
                                <Button size="sm" variant="ghost" onClick={() => publishPage(page.slug)} className="text-green-600">
                                  <Check className="w-4 h-4" />
                                </Button>
                              )}
                              <Button size="sm" variant="ghost" onClick={() => deletePage(page.slug)} className="text-red-600">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {pages.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      No pages found. Create your first SEO page!
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>SEO Templates</CardTitle>
                  <CardDescription>Define templates for each page type</CardDescription>
                </div>
                <Button onClick={seedTemplates} variant="outline">
                  <Plus className="w-4 h-4 mr-2" /> Seed Default Templates
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {templates.map((template, idx) => (
                    <Card key={idx} className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <Badge className="mb-2 capitalize bg-violet-100 text-violet-700">{template.page_type}</Badge>
                            <h4 className="font-semibold text-slate-800">{template.name}</h4>
                            <p className="text-sm text-slate-600 mt-1">
                              <strong>Title:</strong> {template.title_template}
                            </p>
                            <p className="text-sm text-slate-600">
                              <strong>Meta:</strong> {template.meta_description_template?.substring(0, 80)}...
                            </p>
                          </div>
                          <Button size="sm" variant="ghost">
                            <Edit className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {templates.length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      No templates found. Click "Seed Default Templates" to create them.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sitemap Tab */}
          <TabsContent value="sitemap">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="w-5 h-5" />
                  Sitemap Management
                </CardTitle>
                <CardDescription>View and manage your XML sitemaps</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold text-slate-800 mb-2">Sitemap Index</h4>
                    <code className="text-sm text-violet-600">{API_URL}/api/pseo/sitemap/index</code>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pageTypes.map(type => (
                      <div key={type} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium capitalize">{type} Pages</p>
                            <code className="text-xs text-slate-500">/api/pseo/sitemap/{type}.xml</code>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => window.open(`${API_URL}/api/pseo/sitemap/${type}.xml`, '_blank')}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Robots.txt Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea 
                    className="font-mono text-sm h-48"
                    defaultValue={`User-agent: *
Allow: /
Allow: /tools/
Allow: /blog/
Allow: /compare/
Allow: /best/
Disallow: /admin/
Disallow: /api/
Disallow: /dashboard/
Disallow: /login/

Sitemap: ${API_URL}/api/pseo/sitemap/index`}
                  />
                  <Button className="mt-4">Save Robots.txt</Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Internal Linking Rules</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 mb-4">Configure automatic internal linking between page types</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded">
                      <Badge>tool</Badge>
                      <span className="text-slate-600">→</span>
                      <Badge variant="outline">usecase</Badge>
                      <Badge variant="outline">comparison</Badge>
                      <Badge variant="outline">glossary</Badge>
                    </div>
                    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded">
                      <Badge>comparison</Badge>
                      <span className="text-slate-600">→</span>
                      <Badge variant="outline">tool</Badge>
                      <Badge variant="outline">best</Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Add Page Modal */}
        {showPageForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardHeader>
                <CardTitle>Create SEO Page</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Page Type</label>
                    <Select value={pageForm.page_type} onValueChange={(v) => setPageForm({...pageForm, page_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {pageTypes.map(type => (
                          <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Status</label>
                    <Select value={pageForm.status} onValueChange={(v) => setPageForm({...pageForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Slug</label>
                  <Input 
                    placeholder="tools/keyword-research" 
                    value={pageForm.slug}
                    onChange={(e) => setPageForm({...pageForm, slug: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Title</label>
                  <Input 
                    placeholder="Keyword Research Tool - Free SEO Tool" 
                    value={pageForm.title}
                    onChange={(e) => setPageForm({...pageForm, title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Meta Title</label>
                  <Input 
                    placeholder="Keyword Research Tool | MarkeeMix" 
                    value={pageForm.meta_title}
                    onChange={(e) => setPageForm({...pageForm, meta_title: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Meta Description</label>
                  <Textarea 
                    placeholder="Find high-traffic keywords with our free keyword research tool..." 
                    value={pageForm.meta_description}
                    onChange={(e) => setPageForm({...pageForm, meta_description: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">H1</label>
                  <Input 
                    placeholder="Keyword Research Tool" 
                    value={pageForm.h1}
                    onChange={(e) => setPageForm({...pageForm, h1: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Intro</label>
                  <Textarea 
                    placeholder="Looking for the best keywords for your website?..." 
                    value={pageForm.intro}
                    onChange={(e) => setPageForm({...pageForm, intro: e.target.value})}
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowPageForm(false)}>Cancel</Button>
                  <Button onClick={createPage} disabled={loading} className="bg-violet-600 hover:bg-violet-700">
                    Create Page
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bulk Generate Modal */}
        {showBulkForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
              <CardHeader>
                <CardTitle>Bulk Generate Pages</CardTitle>
                <CardDescription>Upload CSV data to generate multiple pages at once</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Page Type</label>
                  <Select value={bulkForm.page_type} onValueChange={(v) => setBulkForm({...bulkForm, page_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {pageTypes.map(type => (
                        <SelectItem key={type} value={type} className="capitalize">{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Variables CSV</label>
                  <Textarea 
                    className="font-mono text-sm h-48"
                    placeholder={`tool_name,tool_category,tool_benefit
Keyword Research,SEO,find high-traffic keywords
Site Audit,SEO,identify SEO issues
Backlink Checker,SEO,analyze backlinks`}
                    value={bulkForm.variables_csv}
                    onChange={(e) => setBulkForm({...bulkForm, variables_csv: e.target.value})}
                  />
                  <p className="text-xs text-slate-500 mt-1">First row = headers, following rows = data</p>
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="auto_publish"
                    checked={bulkForm.auto_publish}
                    onChange={(e) => setBulkForm({...bulkForm, auto_publish: e.target.checked})}
                  />
                  <label htmlFor="auto_publish" className="text-sm">Auto-publish pages</label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowBulkForm(false)}>Cancel</Button>
                  <Button onClick={bulkGenerate} disabled={loading} className="bg-violet-600 hover:bg-violet-700">
                    Generate Pages
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
